/**
 * ProfileStep - Step 1: Customer profile and distributor selection.
 *
 * Provides:
 * - SegmentedControl for customerType (Gospodarstwo / Firma)
 * - Select for distributor (OSD codes)
 * - Select for year (2025/2026)
 * - Select for currentTariff (optional)
 */

import { Stack, Text, SegmentedControl, Select, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import type { UseFormReturnType } from '@mantine/form';
import type { ProsumerFormValues } from '../../types';
import { OSD_CODES, OSD_METADATA, getAvailableTariffsForOSD } from '../../../../utils/osdMetadata';
import type { OSDCode } from '../../../../utils/osdMetadata';

export interface ProfileStepProps {
  form: UseFormReturnType<ProsumerFormValues>;
}

const CUSTOMER_TYPE_OPTIONS = [
  { label: 'Gospodarstwo', value: 'HOUSEHOLD' },
  { label: 'Firma', value: 'BUSINESS' },
];

const DISTRIBUTOR_OPTIONS = OSD_CODES.map((code) => ({
  value: code,
  label: OSD_METADATA[code].shortName,
}));

const YEAR_OPTIONS = [
  { value: '2025', label: '2025' },
  { value: '2026', label: '2026' },
];

const REGION_OPTIONS = [
  { value: 'NORTHEAST', label: 'Północno-wschodnia (Warmia, Mazury, Podlasie)' },
  { value: 'CENTRAL',   label: 'Centralna (Mazowieckie, Łódź, Kujawsko-Pomorskie)' },
  { value: 'SOUTHWEST', label: 'Południowo-zachodnia (Dolnośląskie, Opolskie)' },
  { value: 'SOUTHEAST', label: 'Południowo-wschodnia (Małopolska, Podkarpacie, Lubelskie)' },
  { value: 'NORTHWEST', label: 'Północno-zachodnia (Zachodniopomorskie, Lubuskie)' },
  { value: 'SOUTH',     label: 'Południowa (Śląskie, Świętokrzyskie)' },
];

export function ProfileStep({ form }: ProfileStepProps) {
  const distributor = form.values.distributor;
  const availableTariffs = distributor
    ? getAvailableTariffsForOSD(distributor as OSDCode)
    : [];

  const tariffOptions = [
    { value: '', label: 'Nie wiem / brak' },
    ...availableTariffs.map((t) => ({ value: t, label: t })),
  ];

  // When the distributor changes, sync tariffTypes to the new OSD's available tariffs.
  // This ensures the results comparison always shows all tariffs the customer can actually switch to.
  function handleDistributorChange(value: string | null) {
    const dist = (value ?? '') as OSDCode;
    form.setFieldValue('distributor', dist);
    if (dist) {
      form.setFieldValue('tariffTypes', getAvailableTariffsForOSD(dist));
    }
  }

  return (
    <Stack gap="md">
      <Text fw={600} size="lg">Profil odbiorcy</Text>

      <div>
        <Text size="sm" fw={500} mb={4}>Typ odbiorcy</Text>
        <SegmentedControl
          data={CUSTOMER_TYPE_OPTIONS}
          {...form.getInputProps('customerType')}
          fullWidth
        />
        {form.values.customerType === 'BUSINESS' && (
          <Alert icon={<IconInfoCircle size={16} />} color="yellow" mt="xs">
            Kalkulator wykorzystuje stawki dla gospodarstw domowych. Wyniki dla firm maj&#261; charakter orientacyjny.
          </Alert>
        )}
      </div>

      <Select
        label="Dystrybutor"
        placeholder="Wybierz dystrybutora"
        data={DISTRIBUTOR_OPTIONS}
        value={form.values.distributor}
        error={form.errors.distributor as string | undefined}
        onChange={handleDistributorChange}
      />

      <Select
        label="Rok"
        data={YEAR_OPTIONS}
        value={String(form.values.year)}
        onChange={(val) => form.setFieldValue('year', val ? parseInt(val, 10) : 2026)}
      />

      <Select
        label="Obecna taryfa"
        placeholder="Opcjonalnie"
        data={tariffOptions}
        {...form.getInputProps('currentTariff')}
        clearable
      />

      {form.values.hasPV && (
        <Select
          label="Strefa nasłonecznienia"
          description="Wpływa na szacowaną produkcję energii z paneli PV"
          data={REGION_OPTIONS}
          {...form.getInputProps('region')}
        />
      )}
    </Stack>
  );
}
